import { storeToRefs } from "pinia";
import { STACKED_TYPES, CLUSTERED_TYPES, PIE_TYPES } from "@stores/chartTemplates/chartTemplates";
import { useQuestionListStore } from "@stores/survey/reports/questionList";

/**
 * Composable for resolving chart series/category counts
 * based on survey data (cards, questions, crosstab configuration).
 */
export function useChartDataCounts() {
  const questionListStore = useQuestionListStore();
  const { cardList, surveyQuestions } = storeToRefs(questionListStore);

  function findCard(cardToken) {
    if (!cardToken) return null;
    return cardList.value.find((c) => c.card_token === cardToken) || null;
  }

  function getCardChoicesCount(cardToken) {
    const card = findCard(cardToken);
    if (!card?.choices) return null;
    return Object.keys(card.choices).length;
  }

  function getMatrixItemsCount(cardToken) {
    const card = findCard(cardToken);
    return card?.matrix_items_count || null;
  }

  function isMatrixCard(cardToken) {
    const card = findCard(cardToken);
    return card?.question_type === "matrix";
  }

  function getQuestionChoicesCount(questionId) {
    if (!questionId) return null;
    const question = surveyQuestions.value.find((q) => q.id === parseInt(questionId));
    if (!question?.choices) return null;
    return Object.keys(question.choices).length;
  }

  function getCrosstabCount(binding) {
    if (!binding) return null;

    const hasCrosstab =
      binding.crosstab_enabled || binding.crosstab_question_id || binding.crosstab_groups_tokens?.length > 0;

    if (!hasCrosstab) return 1;

    // Crosstab by question
    if (binding.crosstab_type === "question" || binding.crosstab_question_id) {
      return getQuestionChoicesCount(binding.crosstab_question_id);
    }

    // Crosstab by groups
    if (binding.crosstab_type === "groups" || binding.crosstab_groups_tokens?.length > 0) {
      return binding.crosstab_groups_tokens?.length || null;
    }

    return 1;
  }

  /**
   * Get the number of data series for a chart element.
   */
  function getChartSeriesCount(binding, baseType) {
    const defaultCount = 4;
    if (!binding) return defaultCount;

    // For STACKED charts: series = answer options (segments within each bar)
    if (baseType && STACKED_TYPES.includes(baseType)) {
      return getCardChoicesCount(binding.card_token) || defaultCount;
    }

    // For CLUSTERED / PIE charts: series = crosstab groups
    if (baseType && (CLUSTERED_TYPES.includes(baseType) || PIE_TYPES.includes(baseType))) {
      return getCrosstabCount(binding) || 1;
    }

    return getCrosstabCount(binding) || defaultCount;
  }

  /**
   * Get the number of categories for a chart element.
   */
  function getChartCategoryCount(binding, baseType) {
    const defaultCount = 4;
    if (!binding?.card_token) return defaultCount;

    // For STACKED charts: categories depend on matrix vs crosstabs
    if (baseType && STACKED_TYPES.includes(baseType)) {
      if (isMatrixCard(binding.card_token)) {
        return binding.matrix_item_tokens?.length || getMatrixItemsCount(binding.card_token) || defaultCount;
      }
      return getCrosstabCount(binding) || defaultCount;
    }

    // For CLUSTERED and other charts: categories = answer options
    const choicesCount = getCardChoicesCount(binding.card_token);

    if (choicesCount) {
      if (baseType && PIE_TYPES.includes(baseType)) {
        return Math.min(choicesCount, 8);
      }
      return choicesCount;
    }

    return defaultCount;
  }

  return {
    getChartSeriesCount,
    getChartCategoryCount,
  };
}
